import { createApi } from "@reduxjs/toolkit/query/react";
import dayjs from "dayjs";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { normalizeNfc } from "../../utils/text";

// 型定義（BEのShotSerializerに基づく）
export interface ShotRating {
  user_id: number;
  display_name: string;
  rating: number;
}

export interface Shot {
  id: string; // UUID (title_id)
  shot_name: string; // ショット名（title）
  average_rating: number | null; // 平均評価（rating）
  my_rating: number | null; // リクエストユーザー自身の評価（1-5）
  ratings: ShotRating[]; // 全ユーザーの個別評価（Producer/Directorのみ配列が返る）
  status: number; // ステータス（1:NEW, 2:UPDATE, 3:PRODUCER_PENDING, 4:CLIENT_PENDING, 5:APPROVED, 6:APPROVED_WITH_DELIVERY_FILE, 7:PRODUCER_DONE_CLIENT_UNAPPROVED）
  updated_at: string; // 最終更新日
  is_shared: boolean; // 共有フラグ（can_share）
  thumbnail: string | null; // サムネイル画像URL
  media_file: string | null; // 原本メディアファイルURL（拡張子による種別判定に使う）
  media_stream_url: string | null; // 再生用の変換済みURL（動画は HLS）。未変換なら null
  variation_order: number | null; // バリエーション順序（version_order）
  variation_name: string | null; // バリエーション名（real_name）
  next_delivery_date: string | null; // 次の納品予定日（schedule_date）
  media_item_updated_at: string | null; // メディアアイテムの最終更新日（take_uploaded）
  media_item_count: number; // 紐付く総メディアアイテム数
  max_take_count: number | null; // 最大テイク数（バリエーション名グループ内の最大件数、メディア未登録なら null）
  tag_ids: string[]; // 紐付くProjectTag IDのリスト
}

/**
 * ショットカードの再生に使う URL。
 * 変換済み（動画は HLS）があればそれを使い、無い場合だけ原本にフォールバックする。
 * 原本は数百 MB の MP4 になりうるため、再生用途で media_file を直接使わないこと。
 */
export const getShotPlaybackSrc = (
  shot: Pick<Shot, "media_stream_url" | "media_file">,
): string | null => shot.media_stream_url || shot.media_file;

export interface ShotsData {
  shots: Shot[];
  count: number; // 総ショット数
  has_next: boolean; // 次のページがあるか
  next_offset: number | null; // 次のページのオフセット
  limit: number; // 1ページあたりの取得数
  offset: number; // 現在のオフセット
  filter: "active" | "all"; // 適用されたフィルター
  status_filter: string[] | null; // 適用された status フィルタ
  search: string | null; // 適用された検索語
  sort_by: string; // ソートフィールド
  order: "asc" | "desc"; // ソート順
}

export interface ShotsResponse {
  status: string;
  data: ShotsData;
}

export type ShotFilterType = "active" | "all";
export type ShotSortField = "updated_at" | "created" | "title" | "rating" | "status";
export type SortOrder = "asc" | "desc";

export interface ShotsQueryParams {
  project_id: string;
  filter?: ShotFilterType; // フィルタータイプ（デフォルト: "all"）
  search?: string; // 検索語（タイトルの部分一致）
  sort_by?: ShotSortField; // ソートフィールド（デフォルト: "updated_at"）
  order?: SortOrder; // ソート順（デフォルト: "desc"）
  offset?: number; // ページネーションのオフセット（デフォルト: 0）
  limit?: number; // 1ページあたりの取得数
  status?: string; // カンマ区切りの status 値 (例: "1,2,3,4")
  tag?: string; // タグフィルタ用 ProjectTag UUID
  include_empty?: boolean; // メディアアイテム未登録のショットも含める
}

// ショットステータス更新用の型定義
export interface UpdateShotStatusParams {
  project_id: string;
  shot_id: string;
  status: number;
}

// 単一ショットを返す共通レスポンス型
export interface SingleShotResponse {
  status: string;
  data: { shot: Shot };
}

// ショット作成用の型定義
export interface CreateShotParams {
  project_id: string;
  shot_name: string;
  status?: number;
  tag_ids?: string[];
}

// ショット更新用の型定義
export interface UpdateShotParams {
  project_id: string;
  shot_id: string;
  shot_name?: string;
  status?: number;
  is_shared?: boolean;
  next_delivery_date?: string | null;
  tag_ids?: string[];
}

// ショット詳細取得用の型定義
export interface ShotDetailParams {
  project_id: string;
  shot_id: string;
}

// ショット削除用の型定義
export interface DeleteShotParams {
  project_id: string;
  shot_id: string;
}

// ショット評価用の型定義
export interface RateShotParams {
  project_id: string;
  shot_id: string;
  rating: number; // 1〜5
}

export interface RateShotResponse {
  status: string;
  data: {
    rating: number;
    average_rating: number;
  };
}

export interface DeleteShotRatingParams {
  project_id: string;
  shot_id: string;
}

export interface DeleteShotRatingResponse {
  status: string;
  data: {
    average_rating: number;
  };
}

// ショット間コンテンツ移動用の型定義
export interface MoveShotContentsParams {
  project_id: string;
  shot_id: string; // 移動元ショット
  target_shot_id: string; // 移動先ショット（同一プロジェクト内・自身以外）
  media_item_ids?: string[];
  comment_ids?: string[];
}

export interface MoveShotContentsResponse {
  status: string;
  data: {
    source_shot_id: string;
    target_shot_id: string;
    moved_media_item_ids: string[];
    moved_comment_ids: string[];
  };
}

type UpdateShotBody = Omit<UpdateShotParams, "project_id" | "shot_id">;

/** status を更新する payload は次回提出予定日をクリアする */
const buildStatusUpdateBody = (status: number) => ({
  status,
  next_delivery_date: null as null,
});

/**
 * shot 更新 payload を整形する。
 * - shot_name は NFC 正規化し、Excel コピペ（NFD）と手入力（NFC）の表記ゆれを吸収する。
 * - status を含む場合は次回提出予定日をクリアする。ただし呼び出し側が
 *   next_delivery_date を明示指定した場合はそれを尊重する（ショット一覧テーブルの
 *   一括貼り付け等で status と納品日を同時更新するため）。
 */
const normalizeUpdateShotBody = (body: UpdateShotBody) => {
  const base =
    body.shot_name !== undefined
      ? { ...body, shot_name: normalizeNfc(body.shot_name) }
      : body;
  if (base.status === undefined) return base;
  if (base.next_delivery_date !== undefined) return base;
  return { ...base, next_delivery_date: null as null };
};

export const shotsApi = createApi({
  reducerPath: "shotsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["Shots"],
  endpoints: (builder) => ({
    getShotDetail: builder.query<SingleShotResponse, ShotDetailParams>({
      query: ({ project_id, shot_id }) => `/api/${project_id}/shots/${shot_id}`,
      providesTags: ["Shots"],
      keepUnusedDataFor: 300,
    }),

    getShots: builder.query<ShotsResponse, ShotsQueryParams>({
      query: ({ 
        project_id, 
        filter = "all", 
        search, 
        sort_by = "updated_at", 
        order = "desc",
        offset = 0,
        limit,
        status,
        tag,
        include_empty,
      }) => {
        const params: Record<string, string> = {
          filter,
          sort_by,
          order,
          offset: offset.toString(),
        };
        
        if (search) {
          params.search = search;
        }
        
        if (limit !== undefined) {
          params.limit = limit.toString();
        }

        if (status) {
          params.status = status;
        }

        if (tag) {
          params.tag = tag;
        }

        if (include_empty) {
          params.include_empty = "true";
        }
        
        const queryString = new URLSearchParams(params).toString();
        return `/api/${project_id}/shots?${queryString}`;
      },
      providesTags: ["Shots"],
      keepUnusedDataFor: 300,
    }),

    // ショットのステータスを更新する
    updateShotStatus: builder.mutation<{ status: string }, UpdateShotStatusParams>({
      query: ({ project_id, shot_id, status }) => ({
        url: `/api/${project_id}/shots/${shot_id}`,
        method: "PATCH",
        body: buildStatusUpdateBody(status),
      }),
      invalidatesTags: ["Shots"],
    }),

    // アニメーション付きステータス更新（自動refetchなし — 呼び出し側で手動refetch）
    updateShotStatusAnimated: builder.mutation<{ status: string }, UpdateShotStatusParams>({
      query: ({ project_id, shot_id, status }) => ({
        url: `/api/${project_id}/shots/${shot_id}`,
        method: "PATCH",
        body: buildStatusUpdateBody(status),
      }),
    }),

    // ショットを作成する
    createShot: builder.mutation<SingleShotResponse, CreateShotParams>({
      query: ({ project_id, ...body }) => ({
        url: `/api/${project_id}/shots`,
        method: "POST",
        // shot_name は NFC 正規化して表記ゆれ（NFD/NFC）を吸収する。
        body: { ...body, shot_name: normalizeNfc(body.shot_name) },
      }),
      invalidatesTags: ["Shots"],
    }),

    // ショットを更新する（名前・ステータス・共有フラグ・タグ）
    updateShot: builder.mutation<SingleShotResponse, UpdateShotParams>({
      query: ({ project_id, shot_id, ...body }) => ({
        url: `/api/${project_id}/shots/${shot_id}`,
        method: "PATCH",
        body: normalizeUpdateShotBody(body),
      }),
      invalidatesTags: ["Shots"],
    }),

    // ショットを削除する
    deleteShot: builder.mutation<{ status: string; message: string }, DeleteShotParams>({
      query: ({ project_id, shot_id }) => ({
        url: `/api/${project_id}/shots/${shot_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Shots"],
    }),

    // ショット間のコンテンツ移動（メディアアイテム・コメント）
    // BE 側で「メディアに紐づくコメント」「親コメントの返信」等は自動同梱される
    moveShotContents: builder.mutation<MoveShotContentsResponse, MoveShotContentsParams>({
      query: ({ project_id, shot_id, target_shot_id, media_item_ids, comment_ids }) => ({
        url: `/api/${project_id}/shots/${shot_id}/move`,
        method: "POST",
        body: {
          target_shot_id,
          ...(media_item_ids?.length ? { media_item_ids } : {}),
          ...(comment_ids?.length ? { comment_ids } : {}),
        },
      }),
      invalidatesTags: ["Shots"],
    }),

    // ショット評価登録・更新（upsert）
    rateShot: builder.mutation<RateShotResponse, RateShotParams>({
      query: ({ project_id, shot_id, rating }) => ({
        url: `/api/${project_id}/shots/${shot_id}/rating`,
        method: "PUT",
        body: { rating },
      }),
      // ショット一覧を再取得せず、該当ショットの評価フィールドのみキャッシュを書き換える
      async onQueryStarted({ project_id, shot_id, rating }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const averageRating = data.data.average_rating;
          const state = getState();

          const getShotsArgs = shotsApi.util.selectCachedArgsForQuery(state, "getShots");
          getShotsArgs.forEach((args) => {
            dispatch(
              shotsApi.util.updateQueryData("getShots", args, (draft) => {
                const shot = draft.data.shots.find((s) => s.id === shot_id);
                if (shot) {
                  shot.my_rating = rating;
                  shot.average_rating = averageRating;
                }
              }),
            );
          });

          dispatch(
            shotsApi.util.updateQueryData(
              "getShotDetail",
              { project_id, shot_id },
              (draft) => {
                draft.data.shot.my_rating = rating;
                draft.data.shot.average_rating = averageRating;
              },
            ),
          );
        } catch {
          // サーバー更新に失敗した場合はキャッシュ変更を行わない
        }
      },
    }),

    // ショット評価削除
    deleteShotRating: builder.mutation<DeleteShotRatingResponse, DeleteShotRatingParams>({
      query: ({ project_id, shot_id }) => ({
        url: `/api/${project_id}/shots/${shot_id}/rating`,
        method: "DELETE",
      }),
      // ショット一覧を再取得せず、該当ショットの評価フィールドのみキャッシュを書き換える
      async onQueryStarted({ project_id, shot_id }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const averageRating = data.data.average_rating;
          const state = getState();

          const getShotsArgs = shotsApi.util.selectCachedArgsForQuery(state, "getShots");
          getShotsArgs.forEach((args) => {
            dispatch(
              shotsApi.util.updateQueryData("getShots", args, (draft) => {
                const shot = draft.data.shots.find((s) => s.id === shot_id);
                if (shot) {
                  shot.my_rating = null;
                  shot.average_rating = averageRating;
                }
              }),
            );
          });

          dispatch(
            shotsApi.util.updateQueryData(
              "getShotDetail",
              { project_id, shot_id },
              (draft) => {
                draft.data.shot.my_rating = null;
                draft.data.shot.average_rating = averageRating;
              },
            ),
          );
        } catch {
          // サーバー更新に失敗した場合はキャッシュ変更を行わない
        }
      },
    }),
  }),
});

/** next_delivery_date が当日以降（有効）かどうか判定する */
export const hasValidDeliveryDate = (shot: Shot): boolean => {
  if (!shot.next_delivery_date) return false;
  return dayjs(shot.next_delivery_date).startOf('day').diff(dayjs().startOf('day'), 'day') >= 0;
};

export const {
  useGetShotDetailQuery,
  useLazyGetShotDetailQuery,
  useGetShotsQuery,
  useLazyGetShotsQuery,
  useUpdateShotStatusMutation,
  useUpdateShotStatusAnimatedMutation,
  useCreateShotMutation,
  useUpdateShotMutation,
  useDeleteShotMutation,
  useMoveShotContentsMutation,
  useRateShotMutation,
  useDeleteShotRatingMutation,
} = shotsApi;
